import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import DefaultButton from '../../../../../../global/default-button/default-button';
import { FormField } from '../../../../../../global/form/form-field/form-field';
import { OEToggle } from '../../../../../../global/oe-toggle/oe-toggle';
import { CommonModal } from '../../../../../../global/modal/common/common-modal';
import { ADD_TEXT, CANCEL_TEXT, REQUIRED_FIELD_TEXT } from '../../../../../../../constants/common-labels.constants';
import {
    HUB_MENU_EDIT_LINK_MODAL_TITLE_TEXT,
    HUB_MENU_LINK_ENABLED_TOGGLE_TEXT,
    HUB_MENU_LINK_LABEL_FIELD_TEXT,
    HUB_MENU_LINK_URL_FIELD_TEXT,
    HUB_MENU_NEW_LINK_MODAL_TITLE_TEXT,
    SAVE_CHANGES_BUTTON_TEXT,
} from '../../../../../event-hubs.text';
import { HubMenuSettingsVM } from './hub-menu-settings.viewmodel';
import './hub-menu-link-modal.scss';

type HubMenuLinkModalProps = {
    vm: HubMenuSettingsVM;
};

export const HubMenuLinkModal: React.FC<HubMenuLinkModalProps> = observer(({ vm }) => {
    const draft = vm.getEditingDraft();

    if (!draft) {
        return null;
    }

    const isNewLink = draft.hubMenuLinkId == null;

    return (
        <CommonModal
            isOpen
            onRequestClose={vm.closeLinkModal}
            overlayClassName='hub-menu-link-modal-overlay'
            className='hub-menu-link-modal__modal'
            classNameBody='hub-menu-link-modal__body-wrapper'
            title={<FormattedMessage {...(isNewLink ? HUB_MENU_NEW_LINK_MODAL_TITLE_TEXT : HUB_MENU_EDIT_LINK_MODAL_TITLE_TEXT)} />}
            footer={
                <div className='hub-menu-link-modal__footer'>
                    <DefaultButton
                        modifier='cancel'
                        text={<FormattedMessage {...CANCEL_TEXT} />}
                        onClick={vm.closeLinkModal}
                    />
                    <DefaultButton
                        text={<FormattedMessage {...(isNewLink ? ADD_TEXT : SAVE_CHANGES_BUTTON_TEXT)} />}
                        onClick={vm.onSaveLink}
                        disabled={vm.isSaveLinkDisabled()}
                    />
                </div>
            }
        >
            <div className='hub-menu-link-modal__body'>
                <div className='hub-menu-link-modal__toggle-row'>
                    <OEToggle
                        isSelected={draft.enabled}
                        onClick={vm.toggleDraftEnabled}
                        dataTestId='hub-menu-link-enabled-toggle'
                    />
                    <FormattedMessage {...HUB_MENU_LINK_ENABLED_TOGGLE_TEXT} />
                </div>

                <FormField
                    label={<>
                        <FormattedMessage {...HUB_MENU_LINK_LABEL_FIELD_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                    className='hub-menu-link-modal__field'
                >
                    <input
                        className='oe-form-field__control'
                        type='text'
                        value={draft.label}
                        onChange={event => vm.setDraftLabel(event.target.value)}
                        onBlur={vm.markLabelTouched}
                        data-test-id='hub-menu-link-label'
                    />
                    {vm.getLabelError() && <span className='oe-form-field__error'>{vm.getLabelError()}</span>}
                </FormField>

                <FormField
                    label={<>
                        <FormattedMessage {...HUB_MENU_LINK_URL_FIELD_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                    className='hub-menu-link-modal__field'
                >
                    <input
                        className='oe-form-field__control'
                        type='text'
                        value={draft.externalLink}
                        onChange={event => vm.setDraftUrl(event.target.value)}
                        onBlur={vm.markUrlTouched}
                        data-test-id='hub-menu-link-url'
                    />
                    {vm.getUrlValidationError() && <span className='oe-form-field__error'>{vm.getUrlValidationError()}</span>}
                </FormField>
            </div>
        </CommonModal>
    );
});
